/**
 * <RowDetailDrawer /> — full-record viewer that opens when a row is clicked.
 *
 * Mirrors Airtable's record card: every field on its own line with the
 * field-type icon, label and a renderer that's consistent with the cell
 * renderer used in the grid.
 */

import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CloseRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
} from '@mui/icons-material';
import type { ColumnMeta } from './types';
import { SmartCell } from './SmartCell';
import { FieldTypeIcon } from './icons';
import { formatPlainText } from './format';

interface RowDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  row: any[] | null;
  rowIndex: number;
  totalRows: number;
  columns: ColumnMeta[];
  title?: string;
  onPrev?: () => void;
  onNext?: () => void;
}

export function RowDetailDrawer({
  open,
  onClose,
  row,
  rowIndex,
  totalRows,
  columns,
  title,
  onPrev,
  onNext,
}: RowDetailDrawerProps) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          maxWidth: '100vw',
          bgcolor: theme.tokens.surface.raised,
          borderLeft: `1px solid ${theme.tokens.border.subtle}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${theme.tokens.border.subtle}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                ...theme.typography.eyebrow,
                color: theme.tokens.text.tertiary,
                mb: 0.25,
              }}
            >
              {title || 'Record'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: theme.tokens.text.primary,
              }}
            >
              Row {rowIndex + 1} of {totalRows.toLocaleString()}
            </Typography>
          </Box>
          {onPrev && (
            <Tooltip title="Previous row" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={onPrev}
                  disabled={rowIndex <= 0}
                  sx={{ color: theme.tokens.text.tertiary }}
                >
                  <ChevronLeftRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onNext && (
            <Tooltip title="Next row" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={onNext}
                  disabled={rowIndex >= totalRows - 1}
                  sx={{ color: theme.tokens.text.tertiary }}
                >
                  <ChevronRightRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Close" arrow>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: theme.tokens.text.tertiary }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Fields list */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {row && columns.map((meta, ci) => {
            const value = row[ci];
            return (
              <Box
                key={meta.name}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  pb: 1.25,
                  borderBottom: `1px solid ${theme.tokens.border.subtle}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <FieldTypeIcon
                    type={meta.type}
                    sx={{
                      fontSize: 14,
                      color: theme.tokens.text.tertiary,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      color: theme.tokens.text.tertiary,
                    }}
                  >
                    {meta.label}
                  </Typography>
                </Box>
                {meta.type === 'long_text' || meta.type === 'json' ? (
                  <FullValueBlock value={value} meta={meta} />
                ) : (
                  <Box sx={{ minHeight: 24 }}>
                    <SmartCell value={value} meta={meta} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Drawer>
  );
}

function FullValueBlock({ value, meta }: { value: any; meta: ColumnMeta }) {
  const theme = useTheme();
  if (value === null || value === undefined || value === '') {
    return (
      <Typography
        sx={{
          fontSize: '0.8125rem',
          color: theme.tokens.text.disabled,
          fontStyle: 'italic',
        }}
      >
        —
      </Typography>
    );
  }
  let display: string;
  if (meta.type === 'json') {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      display = JSON.stringify(parsed, null, 2);
    } catch {
      display = String(value);
    }
  } else {
    display = formatPlainText(value, meta);
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        fontFamily: meta.type === 'json' ? theme.typography.fontFamilyMono : 'inherit',
        fontSize: '0.8125rem',
        lineHeight: 1.55,
        color: theme.tokens.text.primary,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        bgcolor: meta.type === 'json' ? theme.tokens.surface.sunken : 'transparent',
        border: meta.type === 'json' ? `1px solid ${theme.tokens.border.subtle}` : 'none',
        borderRadius: meta.type === 'json' ? '6px' : 0,
        p: meta.type === 'json' ? 1 : 0,
      }}
    >
      {display}
    </Box>
  );
}
